import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils"; // if using Tailwind classnames helper
import { BROKER_LOGO } from "@/config/logos";
import { useRecoilState } from "recoil";
import {
  brokerConnectModalAtom,
  brokerSelectModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import { useUserContext } from "@/contexts/userContext";
import { FaPlus } from "react-icons/fa";
import { brokersList, predictionMarketBrokersList } from "../blocks/connection/BrokerList";
import { Badge } from "../ui/badge";
import { useStrategyMode } from "@/contexts/StrategyModeContext";

const brokers = [
  {
    id: "4",
    name: "Alpaca",
    note: "",
    logo: BROKER_LOGO["ALPACA"].DARK.textLogo,
  },

  {
    id: "1",
    name: "Match Trade ",
    note: "",
    logo: BROKER_LOGO["MATCHTRADE"].DARK.textLogo,
  },
  {
    id: "2",
    name: "C-Trader",
    note: "",
    logo: BROKER_LOGO["CTRADER"].DARK.textLogo,
  },
  {
    id: "5",
    name: "Trade-Zero",
    note: "Virtual Trading",
    logo: BROKER_LOGO["TRADEZERO"].DARK.textLogo,
  },
  {
    id: "13",
    name: "Lightspeed",
    note: "",
    logo: BROKER_LOGO["LIGHTSPEED"].DARK.textLogo,
  },
  {
    id: "17",
    name: "Fennel",
    note: "",
    logo: BROKER_LOGO["FENNEL"].DARK.textLogo,
  },
  {
    id: "11",
    name: "Leverate",
    note: "",
    logo: BROKER_LOGO["LEVERATE"].DARK.textLogo,
  },
];

const BrokerSelectionModal = () => {
  const [showSelectionForm, setShowSelectionForm] = useRecoilState(
    brokerSelectModalAtom,
  );
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const { connectedBrokers } = useUserContext();
  const { mode } = useStrategyMode();
  const isPredictionMarket = mode === "prediction_market";

  // In prediction market mode, only show prediction market brokers
  const activeBrokerList = isPredictionMarket
    ? predictionMarketBrokersList
    : brokersList;

  const defaultBrokerId = isPredictionMarket
    ? predictionMarketBrokersList[0]?.id ?? "27"
    : Object.keys(connectedBrokers).length == 1
      ? Object.keys(connectedBrokers)?.[0].toString()
      : "4";

  const [selectedBroker, setSelectedBroker] = useState<string>(defaultBrokerId);

  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom,
  );

  const handleSelect = (id: string) => {
    if (id === "other") return; // don't allow selecting "other"
    setSelectedBroker(id);
  };

  return (
    <Dialog
      open={true}
      onOpenChange={() => {
        setShowSelectionForm({ isOpen: false, callback: () => {} });
      }}
    >
      <form>
        <DialogContent className="max-w-3xl sm:h-auto h-[100dvh]  flex flex-col justify-center">
          <DialogHeader>
            <DialogTitle className="text-[--accent-secondary] text-xl">
              Choose your broker
            </DialogTitle>
            <DialogDescription>
              Select your broker preference to deploy your strategy and start
              trading
            </DialogDescription>
          </DialogHeader>

          <div
            className={cn(
              !isPredictionMarket && Object.keys(connectedBrokers).length == 0
                ? "flex items-center justify-center  py-6"
                : "grid grid-cols-2 sm:grid-cols-3 gap-4 py-6",
            )}
          >
            {activeBrokerList.map((broker) => {
              // In prediction market mode, always show prediction brokers regardless of connection status
              if (!isPredictionMarket && !connectedBrokers[Number(broker.id)]) return;
              const isSelected = selectedBroker === broker.id;

              return (
                <div
                  key={broker.id}
                  onClick={() => {
                    handleSelect(broker.id);
                  }}
                  className={cn(
                    "cursor-pointer relative rounded-lg border p-6 text-center transition-all flex flex-col items-center justify-center min-h-36",
                    isSelected
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-gray-600 hover:border-muted-foreground",
                  )}
                >
                  {broker.logo && (
                    <>
                      <img
                        src={broker.logo}
                        alt={broker.name}
                        className="mx-auto h-8 mb-2"
                      />
                      <p className="text-sm text-muted-foreground">
                        {broker.note}
                      </p>
                    </>
                  )}

                  <div className="flex items-center justify-end gap-2 absolute top-2 right-2">
                    {selectedBroker && (
                      <div className="flex items-center justify-end gap-2">
                        {broker.type === "live" ? (
                          <Badge className="bg-[--marketplace-green]">
                            Live
                          </Badge>
                        ) : (
                          <Badge className="bg-[--accent-primary]">
                            Virtual
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div
              onClick={() => setShowConnectForm({ isOpen: true })}
              className={cn(
                "cursor-pointer rounded-lg border border-border/50 bg-background p-6 text-center flex flex-col items-center  min-h-36 justify-center gap-3 shadow-sm active:scale-95",
              )}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <FaPlus className="h-5 w-5" />
              </div>
              <p className="text-sm text-muted-foreground">
                Don’t see your broker listed?
                <br />
                <span className="font-medium text-primary">
                  Click here to connect
                </span>
              </p>
            </div>
          </div>

          <DialogFooter className="flex   !justify-end flex-row">
            <Button
              className="bg-[--accent-secondary]"
              type="submit"
              onClick={() => {
                setShowSelectionForm({ isOpen: false });
                showSelectionForm?.callback?.(selectedBroker);
              }}
              disabled={!selectedBroker}
            >
              Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default BrokerSelectionModal;
